import { CLOUD, loggedIn, sbRest, sbUrl } from '../cloud/api.js';
import { t } from '../i18n/index.js';
import { S, persist } from '../state.js';
import { RT, render, toast } from '../ui.js';
import { openFromUrl } from './deeplink.js';

/* Web Push on this device. The server (supabase/functions/notify) decides what is due;
   this module only registers the device and keeps its subscription fresh. */
export const PUSH={supported:false,perm:'default',sub:null,ready:false,busy:false,cfg:null,cfgState:'idle'};

const ua=()=>(typeof navigator!=='undefined'&&navigator.userAgent)||'';
export const isIOS=()=>/iPad|iPhone|iPod/.test(ua())||(navigator.platform==='MacIntel'&&navigator.maxTouchPoints>1);
export const isAndroid=()=>/Android/i.test(ua());
export const isMobile=()=>isIOS()||isAndroid();
export const isStandalone=()=>{try{return matchMedia('(display-mode: standalone)').matches||navigator.standalone===true}catch(e){return false}};
export const pushSupported=()=>'serviceWorker' in navigator&&'PushManager' in window&&'Notification' in window;
export const iosNeedsInstall=()=>isIOS()&&!isStandalone();

function rerender(){if(RT.v==='home'||RT.v==='settings'){try{render()}catch(e){}}}
const withTimeout=(p,ms,what)=>Promise.race([p,new Promise((_,no)=>setTimeout(()=>no(new Error(what+' timeout')),ms))]);

export async function registerSW(){
  if(!('serviceWorker' in navigator)||location.protocol==='file:'){PUSH.ready=true;return}
  try{
    await navigator.serviceWorker.register('/sw.js');
    navigator.serviceWorker.addEventListener('message',e=>{if(e.data&&e.data.type==='plume-open')openFromUrl(e.data.url)});
  }catch(e){console.warn('service worker',e)}
  await refreshPush();
  rerender();
}
export async function refreshPush(){
  PUSH.supported=pushSupported();
  if(PUSH.supported){
    PUSH.perm=Notification.permission;
    try{const reg=await navigator.serviceWorker.getRegistration();PUSH.sub=reg?await reg.pushManager.getSubscription():null}
    catch(e){PUSH.sub=null}
  }
  PUSH.ready=true;
}

async function fn(path,opts={}){
  const h={apikey:CLOUD.cfg.key};
  if(opts.body)h['Content-Type']='application/json';
  if(loggedIn()&&path!=='config')h.Authorization='Bearer '+CLOUD.auth.at;
  const r=await fetch(sbUrl('/functions/v1/notify/'+path),{...opts,headers:{...h,...(opts.headers||{})}});
  const d=await r.json().catch(()=>({}));
  if(!r.ok)throw new Error(d.error||('HTTP '+r.status));
  return d;
}
/** {publicKey, email} from the server. cfgState: idle | ok | missing (function not deployed yet) */
export async function pushConfig(){
  if(PUSH.cfg)return PUSH.cfg;
  try{PUSH.cfg=await fn('config');PUSH.cfgState='ok';return PUSH.cfg}
  catch(e){PUSH.cfgState=/404|not found/i.test(e.message)?'missing':'idle';throw e}
}
const keyBytes=b64=>{
  const s=b64.replace(/-/g,'+').replace(/_/g,'/');const bin=atob(s+'='.repeat((4-s.length%4)%4));
  const u=new Uint8Array(bin.length);for(let i=0;i<bin.length;i++)u[i]=bin.charCodeAt(i);return u;
};
async function save(sub){
  const j=sub.toJSON();
  await sbRest('rpc/save_push_subscription',{method:'POST',body:JSON.stringify({p_endpoint:j.endpoint,p_p256dh:j.keys.p256dh,p_auth:j.keys.auth,p_ua:ua().slice(0,300)})});
}
async function subscribe(cfg){
  const reg=await withTimeout(navigator.serviceWorker.ready,8000,'service worker');
  let sub=await reg.pushManager.getSubscription();
  let known=null;try{known=localStorage.getItem('plume.vapid')}catch(e){}
  if(sub&&known&&known!==cfg.publicKey){await sub.unsubscribe().catch(()=>{});sub=null}
  if(!sub)sub=await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:keyBytes(cfg.publicKey)});
  await save(sub);
  try{localStorage.setItem('plume.vapid',cfg.publicKey)}catch(e){}
  return sub;
}

/** Called from a tap: asks for permission, subscribes, registers the device. */
export async function enablePush(){
  if(!loggedIn()){toast(t('n.needLogin'));return false}
  if(!pushSupported()){toast(t('n.unsupported'));return false}
  let perm;
  try{perm=await Notification.requestPermission()}catch(e){perm=Notification.permission}   // first: iOS needs the tap
  PUSH.perm=perm;
  if(perm!=='granted'){toast(t(perm==='denied'?'n.denied':'n.off'));rerender();return false}
  PUSH.busy=true;rerender();
  try{
    const cfg=await pushConfig();
    PUSH.sub=await subscribe(cfg);
    if(S.settings.notify&&S.settings.notify.push===false){S.settings.notify.push=true;persist()}
    toast(t('n.onToast'));
    return true;
  }catch(e){
    console.error('push',e);
    toast(PUSH.cfgState==='missing'?t('n.notReady'):t('n.err',{e:e.message||String(e)}));
    return false;
  }finally{PUSH.busy=false;rerender()}
}
export async function disablePush(){
  try{
    const reg=await navigator.serviceWorker.getRegistration();
    const sub=reg&&await reg.pushManager.getSubscription();
    if(sub){
      await sbRest('rpc/delete_push_subscription',{method:'POST',body:JSON.stringify({p_endpoint:sub.endpoint})}).catch(()=>{});
      await sub.unsubscribe();
    }
    PUSH.sub=null;toast(t('n.offToast'));
  }catch(e){toast(t('n.err',{e:e.message||String(e)}))}
  rerender();
}
/** After sign-in / app start: if this device already allowed notifications, keep it registered. */
export async function syncPush(){
  await refreshPush();
  if(loggedIn()&&PUSH.supported&&PUSH.perm==='granted'){
    try{PUSH.sub=await subscribe(await pushConfig())}catch(e){console.warn('push sync',e)}
  }
  rerender();
}
